import { form, appUrl } from './variables';

/**
 * Cloudflare Turnstile token for spam-gated projects.
 *
 * The API tells us per project whether a token is required
 * (`form.captcha.required` + `form.captcha.site_key`, see WidgetChannelResource).
 * For every other project this module is a no-op and nothing is loaded.
 *
 * NOTE on the "no CDN dependencies" rule: Turnstile cannot be self-hosted — the
 * challenge script must come from challenges.cloudflare.com. It is loaded lazily
 * and ONLY for projects with captcha.required, so the fleet default is unchanged.
 *
 * Tokens are single-use: every call renders/executes a fresh challenge.
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TIMEOUT_MS = 12000;
// Brokered challenges may show the managed-mode checkbox and wait for a click.
const BROKER_TIMEOUT_MS = 45000;
let brokerSeq = 0;

let scriptPromise = null;

/**
 * purpose 'lead'  → token wanted when the project requires captcha on submissions.
 * purpose 'otp'   → also when the API asks for a token on every OTP send
 *                   (captcha.otp_required, fleet-wide SMS-pumping guard).
 */
function captchaConfig(purpose = 'lead') {
    const c = form?.captcha;
    if (!c || !c.site_key) return null;
    const wanted = purpose === 'otp' ? (c.required || c.otp_required) : c.required;
    return wanted ? c : null;
}

export function isCaptchaRequired() {
    return !!captchaConfig('lead');
}

function loadScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')); };
        document.head.appendChild(s);
    });
    return scriptPromise;
}

function container() {
    let el = document.getElementById('pp-turnstile');
    if (!el) {
        el = document.createElement('div');
        el.id = 'pp-turnstile';
        // Managed mode is usually invisible; when Cloudflare escalates to an
        // interactive check the iframe renders here, centered at the bottom.
        el.style.cssText = 'position:fixed;left:50%;bottom:12px;transform:translateX(-50%);z-index:2147483647;';
        document.body.appendChild(el);
    }
    return el;
}

/**
 * Resolve to a token string, or null when the project does not require captcha.
 * Rejects if the challenge could not be completed (script blocked, timeout, error)
 * so the caller surfaces "please try again" instead of a silent 422.
 */
export async function getTurnstileToken(purpose = 'lead') {
    const cfg = captchaConfig(purpose);
    if (!cfg) return null;

    // The site key only allows our own hostnames. On a client site (widget.js
    // embed) render the challenge in an iframe on the widget host instead —
    // Turnstile error 110200 "domain not allowed" otherwise.
    const brokerOrigin = widgetOrigin();
    if (brokerOrigin && brokerOrigin !== window.location.origin) {
        return brokeredToken(cfg, purpose, brokerOrigin);
    }

    await loadScript();

    return new Promise((resolve, reject) => {
        const el = container();
        el.innerHTML = '';
        let done = false;
        const timer = setTimeout(() => {
            if (done) return;
            done = true;
            try { window.turnstile.remove(widgetId); } catch (e) { /* noop */ }
            reject(new Error('turnstile timeout'));
        }, TIMEOUT_MS);
        const finish = (fn) => (v) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            try { window.turnstile.remove(widgetId); } catch (e) { /* noop */ }
            el.innerHTML = '';
            fn(v);
        };
        let widgetId;
        try {
            widgetId = window.turnstile.render(el, {
                sitekey: cfg.site_key,
                action: purpose,
                callback: finish(resolve),
                'error-callback': finish(() => reject(new Error('turnstile error'))),
                'expired-callback': finish(() => reject(new Error('turnstile expired'))),
            });
        } catch (e) {
            finish(reject)(e);
        }
    });
}

function widgetOrigin() {
    try { return appUrl ? new URL(appUrl).origin : null; } catch (e) { return null; }
}

function brokeredToken(cfg, purpose, origin) {
    return new Promise((resolve, reject) => {
        const el = container();
        el.innerHTML = '';
        const id = 'pp' + Date.now().toString(36) + (brokerSeq++);
        const frame = document.createElement('iframe');
        frame.src = origin + '/turnstile.html?' + new URLSearchParams({
            site_key: cfg.site_key, action: purpose, id, parent: window.location.origin,
        }).toString();
        frame.title = 'Verification';
        frame.setAttribute('aria-hidden', 'true');
        // Big enough for the managed-mode checkbox (300x65), transparent otherwise.
        frame.style.cssText = 'width:300px;height:65px;border:0;background:transparent;display:block;';
        let done = false;
        const finish = (fn) => (v) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            try { frame.remove(); } catch (e) { /* noop */ }
            fn(v);
        };
        const timer = setTimeout(finish(() => reject(new Error('turnstile timeout'))), BROKER_TIMEOUT_MS);
        const onMessage = (ev) => {
            if (ev.origin !== origin || !ev.data || ev.data.type !== 'pp-turnstile' || ev.data.id !== id) return;
            if (ev.data.token) finish(resolve)(ev.data.token);
            else finish(() => reject(new Error('turnstile ' + (ev.data.error || 'error'))))();
        };
        window.addEventListener('message', onMessage);
        el.appendChild(frame);
    });
}

/** Merge a token into a request payload when the project requires one. */
export async function withTurnstile(data, purpose = 'lead') {
    let token = null;
    try {
        token = await getTurnstileToken(purpose);
    } catch (e) {
        // Fail OPEN for OTP: a challenge that cannot run on this host (Turnstile
        // 110200 "domain not allowed" on a client site, script blocked, timeout)
        // must never stop a real visitor's code. The API decides what to do with a
        // missing token. Lead submissions keep the strict behaviour.
        if (purpose !== 'otp') throw e;
        try { console.warn('[widget] turnstile unavailable for OTP:', e && e.message); } catch (_) { /* noop */ }
    }
    return token ? { ...(data || {}), cf_turnstile_token: token } : data;
}
